import { CombinedIcon, TextCell, TextIconCell } from "@similarweb/pro-logic-components";
import { Type } from "@similarweb/ui-components";

import { NOT_AVAILABLE } from "app/filters/const";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter } from "app/filters/ngFilters";
import type { PrimitiveListCellValue } from "app/pages/sales-intelligence/common-components/table-cells";
import {
    createLocationCodesCellValue,
    createPrimitiveListCellValue,
    ListCell,
    LocationGroupCell,
    PriceCell,
    renderAbbrNumberWithThresholdCellFactory,
    renderDateCellFactory,
    renderRatingCellFactory,
} from "app/pages/sales-intelligence/common-components/table-cells";
import TechnologiesCell from "app/pages/sales-intelligence/common-components/table-cells/components/TechnologiesCell/TechnologiesCell";
import { createColumnFactoryList } from "app/pages/sales-intelligence/common-components/search-results-table/utils/columns";
import { MIN_RELIABLE_METRIC_VALUE } from "app/pages/sales-intelligence/constants/threshold";
import BusinessModelsCell from "app/pages/sales-intelligence/modules/apps-search/components/cells/BusinessModelsCell";
import {
    createDynamicColumnFactory,
    createRegularColumnFactory,
} from "app/pages/sales-intelligence/modules/apps-search/configuration/columns/utils";
import type { SdkRowDto } from "app/pages/sales-intelligence/modules/apps-search/types/rows";
import type { AppsColumnListConfig } from "app/pages/sales-intelligence/modules/apps-search/types/table";
import { appOsToStoreMap } from "app/pages/sales-intelligence/modules/apps-search-filters/constants";
import { SiUpgradeCellContainer } from "app/pages/sales-intelligence/modules/leads-search/containers/SiUpgradeCellContainer/SiUpgradeCellContainer";
import AppCellContent from "app/pages/sales-intelligence/pages/find-apps/components/cells/AppCellContent";
import solutionUrlsService from "app/pages/sales-intelligence/services/solution-urls";
import type { IFindAppsCommonRowDto } from "app/pages/sales-intelligence/store/find-apps/types/dto";

type Options = {
    readonly i18nKeyModifier: string;
};

const abbrNumber = abbrNumberFilter();
const renderAbbrNumberWithThresholdCell = renderAbbrNumberWithThresholdCellFactory({ min: MIN_RELIABLE_METRIC_VALUE });

const formatSdksArray = (value: readonly PrimitiveListCellValue[]) =>
    translate("si.find_apps.table.column.content.sdk", { count: value.length });

export const createSharedColumnList = ({ i18nKeyModifier }: Options): AppsColumnListConfig<IFindAppsCommonRowDto> =>
    createColumnFactoryList([
        [
            "app",
            createRegularColumnFactory({
                dataIndex: "app",
                width: 284,
                render: (value: IFindAppsCommonRowDto["app"], row) => (
                    <TextIconCell
                        fontVariant="input2"
                        icon={
                            <CombinedIcon
                                type="itemIcon"
                                iconType={Type.Website}
                                iconSrc={row.icon}
                            />
                        }
                    >
                        <AppCellContent
                            overviewLink={solutionUrlsService.getAppAnalysisUrl(
                                row.app_id,
                                appOsToStoreMap[row.market],
                            )}
                            tooltipContent={translate("si.find_apps.table.column.tooltip.app-link")}
                        >
                            {value}
                        </AppCellContent>
                    </TextIconCell>
                ),
            }),
        ],
        [
            "developer_name",
            createRegularColumnFactory({
                dataIndex: "developer_name",
                ellipsis: true,
            }),
        ],
        [
            "country",
            createRegularColumnFactory({
                dataIndex: "country",
                render: (value: IFindAppsCommonRowDto["country"], _record, index) => {
                    const locationCodes = createLocationCodesCellValue(value);

                    return (
                        <SiUpgradeCellContainer isUpgradeVisible={locationCodes[0] === -1}>
                            <LocationGroupCell
                                locationCodes={locationCodes}
                                recordIndex={index}
                            />
                        </SiUpgradeCellContainer>
                    );
                },
            }),
        ],
        [
            "website",
            createRegularColumnFactory({
                dataIndex: "website",
                ellipsis: true,
            }),
        ],
        [
            "monthly_downloads",
            createRegularColumnFactory({
                dataIndex: "monthly_downloads",
                tooltipTitle: translate(`si.find_apps.table.column.tooltip-title.${i18nKeyModifier}.monthly_downloads`),
                align: "end",
                render: renderAbbrNumberWithThresholdCell,
            }),
        ],
        [
            "monthly_active_users",
            createRegularColumnFactory({
                dataIndex: "monthly_active_users",
                align: "end",
                render: (value: IFindAppsCommonRowDto["monthly_active_users"]) => (
                    <TextCell>{value === 0 ? NOT_AVAILABLE : abbrNumber(value)}</TextCell>
                ),
            }),
        ],
        [
            "last_updated",
            createRegularColumnFactory({
                dataIndex: "last_updated",
                render: (value: IFindAppsCommonRowDto["last_updated"]) => renderDateCellFactory()(value),
            }),
        ],
        [
            "release_date",
            createRegularColumnFactory({
                dataIndex: "release_date",
                render: (value: IFindAppsCommonRowDto["release_date"]) =>
                    value == null ? <TextCell>{NOT_AVAILABLE}</TextCell> : renderDateCellFactory()(value),
            }),
        ],
        [
            "price",
            createRegularColumnFactory({
                dataIndex: "price",
                align: "end",
                width: 95,
                render: (value: IFindAppsCommonRowDto["price"]) => <PriceCell value={value} />,
            }),
        ],
        [
            "rating",
            createRegularColumnFactory({
                dataIndex: "rating",
                align: "end",
                width: 95,
                render: renderRatingCellFactory(),
            }),
        ],
        [
            "number_of_ratings",
            createRegularColumnFactory({
                dataIndex: "number_of_ratings",
                align: "end",
                render: renderAbbrNumberWithThresholdCell,
            }),
        ],
        [
            "sdk",
            createDynamicColumnFactory({
                width: 150,
                render: (value: IFindAppsCommonRowDto[SdkRowDto]) => (
                    <TechnologiesCell
                        value={value}
                        formatArrayValue={formatSdksArray}
                    />
                ),
            }),
        ],
        [
            "sdkCategory",
            createDynamicColumnFactory({
                width: 150,
                render: (value: IFindAppsCommonRowDto[SdkRowDto]) => (
                    <TechnologiesCell
                        value={value}
                        formatArrayValue={formatSdksArray}
                    />
                ),
            }),
        ],
        [
            "permissions",
            createRegularColumnFactory({
                dataIndex: "permissions",
                render: (value: IFindAppsCommonRowDto["permissions"]) => (
                    <ListCell
                        value={value}
                        dataAutomation="app-permissions"
                    >
                        {createPrimitiveListCellValue({ value })}
                    </ListCell>
                ),
            }),
        ],
        [
            "business_models",
            createRegularColumnFactory({
                dataIndex: "business_models",
                render: (value: IFindAppsCommonRowDto["business_models"]) => <BusinessModelsCell value={value} />,
            }),
        ],
        [
            "ads_sellers",
            createRegularColumnFactory({
                dataIndex: "ads_sellers",
                render: (value: IFindAppsCommonRowDto["ads_sellers"]) => (
                    <ListCell
                        value={value}
                        dataAutomation="ads-sellers"
                    >
                        {createPrimitiveListCellValue({ value })}
                    </ListCell>
                ),
            }),
        ],
        [
            "iab_categories_paths",
            createRegularColumnFactory({
                dataIndex: "iab_categories_paths",
                width: 250,
                render: (value: IFindAppsCommonRowDto["iab_categories_paths"]) => (
                    <ListCell
                        value={value}
                        dataAutomation="iab_categories_paths"
                        tooltipPlacement="left"
                    >
                        {createPrimitiveListCellValue({ value })}
                    </ListCell>
                ),
            }),
        ],
        [
            "email",
            createRegularColumnFactory({
                dataIndex: "email",
                ellipsis: true,
            }),
        ],
    ]);
